import React from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import {Heart, LogOut, MapPin, Package, Receipt, Settings, ShoppingCart, User, ChevronRight} from 'lucide-react'
import { lumi } from "../lib/lumi"

type MemberSidebarActiveId = "overview" | "orders" | "points" | "wishlist" | "cart" | "addresses" | "settings"

export default function MemberSidebar(props: {
  currentLang: "zh" | "en"
  activeId: MemberSidebarActiveId
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const user = lumi.auth.user
  // V2 側邊欄：在 V2 路由（/cart/v2、/member/v2/*）中使用時該保留在 V2 世界
  const isV2 = location.pathname.includes('/v2')
  const memberBase = isV2 ? `/${props.currentLang}/member/v2` : `/${props.currentLang}/member`
  const cartHref = isV2 ? `/${props.currentLang}/cart/v2` : `/${props.currentLang}/cart`

  const labels = {
    zh: {
      memberLevel: "一般會員",
      overview: "帳戶總覽",
      orders: "歷史訂單",
      points: "點數交易記錄",
      wishlist: "收藏清單",
      cart: "我的購物車",
      addresses: "常用地址",
      settings: "帳戶設定",
      logout: "登出"
    },
    en: {
      memberLevel: "Member",
      overview: "Overview",
      orders: "Orders",
      points: "Points History",
      wishlist: "Wishlist",
      cart: "My Cart",
      addresses: "Addresses",
      settings: "Settings",
      logout: "Logout"
    }
  } as const

  const t = labels[props.currentLang]

  const items: Array<{
    id: MemberSidebarActiveId
    icon: React.ComponentType<{ size?: number; strokeWidth?: number }>
    label: string
    href: string
  }> = [
    { id: "overview", icon: User, label: t.overview, href: `${memberBase}/overview` },
    { id: "orders", icon: Package, label: t.orders, href: `${memberBase}/orders` },
    { id: "points", icon: Receipt, label: t.points, href: `${memberBase}/points` },
    { id: "wishlist", icon: Heart, label: t.wishlist, href: `${memberBase}/wishlist` },
    { id: "cart", icon: ShoppingCart, label: t.cart, href: cartHref },
    { id: "addresses", icon: MapPin, label: t.addresses, href: `${memberBase}/addresses` },
    { id: "settings", icon: Settings, label: t.settings, href: `${memberBase}/settings` }
  ]

  const handleLogout = async () => {
    try {
      await lumi.auth.signOut()
    } finally {
      navigate(`/${props.currentLang}`)
    }
  }

  return (
    <aside className="w-full lg:w-80 flex-shrink-0 space-y-8">
      <div className="bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-800 p-6 relative overflow-hidden group">
        <div className="absolute top-0 left-0 w-1 h-full bg-black dark:bg-gray-200 transform origin-bottom scale-y-0 group-hover:scale-y-100 transition-transform duration-300" />

        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center text-2xl text-gray-500 dark:text-gray-400 font-light overflow-hidden">
            {user?.picture ? (
              <img src={user.picture} alt={user?.userName || "User"} className="w-full h-full object-cover rounded-full" />
            ) : (
              (user?.userName || "U").charAt(0).toUpperCase()
            )}
          </div>
          <div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">{user?.userName || "Member"}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-light tracking-wide">{t.memberLevel}</p>
          </div>
        </div>

        <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
          <p className="text-xs text-gray-400 break-all font-mono">{user?.email || ""}</p>
        </div>
      </div>

      <nav className="flex flex-col space-y-1">
        {items.map((item) => {
          const isActive = props.activeId === item.id
          const Icon = item.icon
          return (
            <Link
              key={item.id}
              to={item.href}
              className={`flex items-center justify-between px-6 py-4 transition-all duration-200 border-l-2 ${
                isActive
                  ? "bg-gray-50 dark:bg-gray-900 border-black dark:border-white text-black dark:text-white"
                  : "border-transparent text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-900 hover:text-gray-900 dark:hover:text-gray-200"
              }`}
            >
              <div className="flex items-center space-x-3">
                <Icon size={18} strokeWidth={1.5} />
                <span className="text-sm tracking-wide">{item.label}</span>
              </div>
              <ChevronRight
                size={16}
                className={`transition-transform duration-200 ${isActive ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}`}
              />
            </Link>
          )
        })}

        <button
          onClick={handleLogout}
          className="flex items-center justify-between px-6 py-4 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 hover:text-red-600 transition-all duration-200 border-l-2 border-transparent mt-8"
        >
          <div className="flex items-center space-x-3">
            <LogOut size={18} strokeWidth={1.5} />
            <span className="text-sm tracking-wide">{t.logout}</span>
          </div>
        </button>
      </nav>
    </aside>
  )
}
